import type { ReactNode } from "react"
import moment from "moment"
import { get, isEmpty } from "lodash"
import OPTooltip from "components/material/tooltip"
import { getIntradayPriceState, INTRADAY_PRICE_STATES } from "helpers/intraday-price-state"
import { NIL_DISPLAY } from "helpers/metrics/nil-display"
import { TOOLTIP_ENTER_DELAY_MS } from "helpers/constants"

interface ContributorItem {
  children?: Record<string, unknown>
  values?: Record<string, unknown>
  notHeld?: boolean
  isNoLongerHeld?: boolean
}

interface TransformedMetric {
  /** Instrument-suffixed path into contributor values, e.g. "intraday-rebalanced.priceAsOf". */
  dataPath: string
}

function isSecurityRow(item: ContributorItem, isAggregation: boolean): boolean {
  return !isAggregation && !item.notHeld && !item.isNoLongerHeld && isEmpty(item.children)
}

function getPriceAsOfAtSiblingPath(item: ContributorItem, metric: TransformedMetric): string | undefined {
  const intradayPrefix = metric.dataPath.split(".")[0]
  const priceAsOf: unknown = get(item.values, [intradayPrefix, "priceAsOf"])
  return typeof priceAsOf === "string" ? priceAsOf : undefined
}

/**
 * Guards the intraday "Price Change" cell: a row carried forward on a prior close
 * shows an explicit "as of prior close" dash instead of a stale percentage, while a
 * genuine 0% move on a today-priced row still renders as 0.0%.
 */
export function enhanceIntradayChangeValue(
  item: ContributorItem,
  formattedValue: ReactNode,
  metric: TransformedMetric,
  isAggregation: boolean,
): ReactNode {
  if (!isSecurityRow(item, isAggregation) || formattedValue === NIL_DISPLAY) {
    return formattedValue
  }
  const priceAsOf = getPriceAsOfAtSiblingPath(item, metric)
  if (getIntradayPriceState(priceAsOf) !== INTRADAY_PRICE_STATES.PRIOR) {
    return formattedValue
  }
  // "Prior" means no update on the viewer's local date, not necessarily a close,
  // so state the last-priced date rather than asserting one.
  const priceMoment = priceAsOf == null ? null : moment(priceAsOf, moment.ISO_8601, true)
  const title = priceMoment?.isValid()
    ? `Last priced ${priceMoment.format("MMM D, LT")} — no update today`
    : "Not priced today"
  return (
    <OPTooltip
      enterDelay={TOOLTIP_ENTER_DELAY_MS}
      title={title}
      placement="bottom-start"
    >
      <span className="intraday-change-prior">—</span>
    </OPTooltip>
  )
}
